<template>
    <Card overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />

                <Multiselect
                    :key="brandsKey"
                    :options="brandOptions"
                    :placeholder="$t('salesProductDetails.brand')"
                    :selected-options-label="$t('salesProductDetails.selectedBrands')"
                    track-by="value"
                    label="text"
                    select-label=""
                    :disabled="loading || filtersLoading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="selectedBrands = $event"
                />

                <DropdownFilter v-model="conversion" :items="conversionItems" :prefix="`${$t('salesSummary.conversion')}: `" :disabled="loading" />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :fetch-options-function="fetchGroups"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <DataTable
            :items="filteredItems"
            :fields="fields"
            :loading="loading"
            :pagination="true"
            :per-page-values="perPageValues"
            :per-page.sync="perPage"
            :current-page.sync="page"
            :overflow="true"
            :error="error"
            :header="$t('salesProductDetails.title')"
            show-export-button
            header-id="sales-product-details-table"
            @update:error="error = null"
        >
            <template #cell(date)="{ item }">
                {{ $format.date(item.date) }}
            </template>

            <template #cell(transactionId)="{ item }">
                {{ item.transactionId || '—' }}
            </template>

            <template #cell(product)="{ item }">
                <template v-if="item.type === 'not_matched'">
                    <span class="text-nowrap">
                        {{ $t('salesProductDetails.unknownProduct') }}
                        <Tooltip :text="$t('salesProductDetails.unknownProductDescription')" />
                    </span>
                </template>
                <template v-else-if="item.type === 'filtered'">
                    <span class="text-nowrap">
                        {{ $t('salesProductDetails.otherProducerProduct') }}
                        <Tooltip :text="$t('salesProductDetails.otherProducerProductDescription')" />
                    </span>
                </template>
                <template v-else>
                    {{ item.product || '—' }}
                </template>
            </template>

            <template #cell(sku)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? item.sku || '—' : '—' }}
            </template>

            <template #cell(ean)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? item.ean || '—' : '—' }}
            </template>

            <template #cell(brand)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? item.brand || '—' : '—' }}
            </template>

            <template #cell(conversionTime)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? formatDuration(item.conversionTime) : '—' }}
            </template>

            <template #cell(quantity)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? $format.number(item.quantity, 0) : '—' }}
            </template>

            <template #cell(amount)="{ item }">
                {{ item.type !== 'not_matched' && item.type !== 'filtered' ? $format.currency(item.value) : '—' }}
            </template>
        </DataTable>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import salesReportMixin from '../salesReportMixin';
    import { formatDuration, fetchSources, fetchGroups } from '../utils';

    export default {
        name: 'SalesProductDetails',
        mixins: [salesReportMixin],
        components: { Card, DropdownFilter, Multiselect, DataTable, Tooltip },
        data() {
            return {
                requestProductDetails: this.$req('reportsSales/ProductDetails'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),

                sources: [],
                sourcesLoading: false,
                selectedBrands: [],
                allData: [],
                groups: [],
                groupsLoading: false,

                conversion: 24,
                conversionItems: ['all', 24],
                showProductGroupsResults: false,

                page: 1,
                perPage: 10,
                perPageValues: [10, 25, 50, 100],

                error: null,
            };
        },
        computed: {
            brandsKey() {
                return `${this.timeRangeString}-${this.sources.join(',')}-${this.groups.join(',')}-${this.conversion}`;
            },
            loading() {
                return this.requestProductDetails.getLoading() || this.sourcesLoading || this.groupsLoading;
            },
            filtersLoading() {
                return this.sourcesLoading || this.groupsLoading;
            },
            brandOptions() {
                if (!this.allData || !this.allData.length) return [];
                const uniqueBrands = [...new Set(this.allData.map(item => item.brand || ''))];
                const lackOfLabel = this.$t('lackOf').toLowerCase();
                return uniqueBrands.map(brand => ({
                    value: brand,
                    text: brand || lackOfLabel,
                }));
            },
            filteredItems() {
                if (!this.allData || !this.allData.length) return [];

                let filtered = [...this.allData];

                // Filter by selected brands (only if some but not all are selected)
                if (this.selectedBrands && this.selectedBrands.length > 0 && this.selectedBrands.length < this.brandOptions.length) {
                    filtered = filtered.filter(item => this.selectedBrands.includes(item.brand || ''));
                }

                return filtered;
            },
            fields() {
                return [
                    {
                        key: 'date',
                        label: this.$t('salesProductDetails.date'),
                        sortable: true,
                        class: 'text-left',
                        exportable: true,
                        exportFormatter: value => this.$format.date(value),
                    },
                    { key: 'transactionId', label: this.$t('salesProductDetails.transactionIdShort'), sortable: true, class: 'text-left', exportable: true },
                    { key: 'product', label: this.$t('salesProductDetails.productName'), sortable: true, class: 'text-left', exportable: true },
                    { key: 'sku', label: this.$t('salesProductDetails.sku'), sortable: true, class: 'text-left', exportable: true },
                    { key: 'ean', label: this.$t('salesProductDetails.ean'), sortable: true, class: 'text-left', exportable: true },
                    { key: 'brand', label: this.$t('salesProductDetails.brand'), sortable: true, class: 'text-left', exportable: true },
                    {
                        key: 'conversionTime',
                        label: this.$t('salesProductDetails.conversionTime'),
                        sortable: true,
                        class: 'text-left',
                        exportable: true,
                        exportFormatter: value => this.formatDuration(value),
                    },
                    {
                        key: 'quantity',
                        label: this.$t('salesProductDetails.quantity'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.number(value, 0),
                    },
                    {
                        key: 'amount',
                        label: this.$t('salesProductDetails.amount'),
                        sortable: true,
                        class: 'text-right',
                        exportable: true,
                        exportFormatter: value => this.$format.currency(value),
                    },
                ];
            },
            apiParams() {
                const { startDate, endDate } = this.timeRange || {};
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { startDate, endDate };
                if (this.sources?.length) requestParams.sources = this.sources.join(',');
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                if (this.conversion !== 'all') requestParams.conversionTime = Number(this.conversion);
                return { urlParams, requestParams };
            },
        },
        watch: {
            spaceId() {
                this.resetComponent();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.fetchData();
                },
            },
            allData: {
                handler(newData) {
                    if (newData && newData.length > 0 && this.selectedBrands.length === 0) {
                        this.$nextTick(() => {
                            this.selectedBrands = this.brandOptions.map(option => option.value);
                        });
                    }
                },
                immediate: true,
            },
        },
        methods: {
            resetFilters() {
                this.conversion = 24;
                this.showProductGroupsResults = false;
                this.resetComponent();
            },
            resetComponent() {
                this.sources = [];
                this.selectedBrands = [];
                this.allData = [];
                this.groups = [];
                this.requestProductDetails.resetAll?.();
                this.fetchData();
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
            async fetchGroups({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
            async fetchData() {
                if (!this.spaceId || !this.timeRange || this.filtersLoading) return;

                try {
                    const { startDate, endDate } = this.timeRange;
                    const params = {
                        urlParams: { spaceId: this.spaceId },
                        requestParams: {
                            startDate,
                            endDate,
                            conversionTime: this.conversion === 'all' ? null : Number(this.conversion),
                        },
                    };

                    if (this.sources?.length) {
                        params.requestParams.sources = this.sources.join(',');
                    }
                    if (this.showProductGroupsResults && this.groups?.length) {
                        params.requestParams.groups = this.groups.join(',');
                    }

                    this.error = null;

                    const data = await this.requestProductDetails.get(params);
                    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

                    this.allData = items.map(row => ({
                        ...row,
                        transactionId: row.id,
                    }));
                } catch (error) {
                    this.error = error;
                    this.allData = [];
                }
            },
            formatDuration(milliseconds) {
                return formatDuration(milliseconds);
            },
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
